import React from "react";
import ReactDOM from "react-dom";
import { HiGift, HiStar, HiShoppingCart, HiX, HiQrcode, HiOfficeBuilding } from "react-icons/hi";
import type { LoyaltyRedemption, LoyaltyReward } from "../../service/LoyaltyService";

// Opened by tapping a reward the customer can afford. Offers the ways this
// store allows: "Add to cart" (online redemption — only while online ordering
// is on) and "Redeem in store". In-store is information only — nothing
// touches the balance here; staff redeem it on the till's Rewards screen once
// the customer is linked (QR on the till's screen, or their email).

type Props = {
  reward: LoyaltyReward;
  productName: string;
  image?: string;
  /** Visible points the customer has free (balance minus rewards in the cart). */
  available: number;
  email: string;
  redemption: LoyaltyRedemption;
  /** Online redemption is allowed AND the store is taking online orders. */
  canOnline: boolean;
  onAddToCart: () => void;
  onScan: () => void;
  onClose: () => void;
  /** Accent CSS variables from the Rewards page (the modal is portalled out of it). */
  vars: React.CSSProperties;
  textColor: string;
  cardColor: string;
  borderColor: string;
};

const css = `
.rdm-back { position: fixed; left: 0; top: 0; right: 0; bottom: 0; z-index: 9000; background: rgba(0,0,0,.5); display: flex; align-items: flex-end; justify-content: center; overscroll-behavior: contain; -webkit-font-smoothing: antialiased; }
@media (min-width: 560px) { .rdm-back { align-items: center; } }
.rdm {
  width: 100%; max-width: 440px; max-height: 92vh; overflow-y: auto; box-sizing: border-box;
  border-radius: 22px 22px 0 0; padding: 0 0 calc(env(safe-area-inset-bottom) + 18px); text-align: left;
  box-shadow: 0 -10px 40px rgba(0,0,0,.25);
}
@media (min-width: 560px) { .rdm { border-radius: 22px; padding-bottom: 18px; } }
.rdm-figure { position: relative; width: 100%; aspect-ratio: 16 / 9; background: var(--rw-accent-soft); display: flex; align-items: center; justify-content: center; border-radius: inherit; border-bottom-left-radius: 0; border-bottom-right-radius: 0; overflow: hidden; }
.rdm-figure img { position: absolute; left: 0; top: 0; width: 100%; height: 100%; object-fit: cover; }
.rdm-figure .rdm-gift { font-size: 44px; color: var(--rw-accent); }
.rdm-x { position: absolute; right: 12px; top: 12px; width: 36px; height: 36px; border-radius: 50%; border: none; background: rgba(0,0,0,.45); color: #fff; font-size: 19px; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 1; }
.rdm-body { padding: 18px 20px 0; }
.rdm-name { font-size: 21px; font-weight: 800; letter-spacing: -0.4px; margin: 0 0 8px; line-height: 1.25; }
.rdm-pts { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 13px; opacity: .85; margin-bottom: 18px; }
.rdm-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 13px; font-weight: 800; color: var(--rw-accent); background: var(--rw-accent-soft); border: 1px solid var(--rw-accent-border); padding: 3px 10px; border-radius: 999px; }
.rdm-q { font-size: 11px; font-weight: 700; letter-spacing: .1em; opacity: .55; margin: 0 0 10px; }
.rdm-opt {
  width: 100%; display: flex; align-items: center; gap: 13px; text-align: left; font-family: inherit; color: inherit;
  border-radius: 14px; padding: 14px 15px; margin-bottom: 10px; cursor: pointer; background: transparent;
  border: 1px solid var(--rdm-border); transition: transform .12s ease, border-color .12s ease;
}
.rdm-opt:active { transform: scale(.99); }
.rdm-opt.primary { border-color: var(--rw-accent-border); background: var(--rw-accent-soft); }
.rdm-opt-icon { width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 20px; background: var(--rw-accent); color: #fff; }
.rdm-opt-text { flex: 1; min-width: 0; }
.rdm-opt b { display: block; font-size: 15px; margin-bottom: 2px; }
.rdm-opt-sub { display: block; font-size: 12.5px; opacity: .65; line-height: 1.4; }
.rdm-opt-go { font-size: 22px; font-weight: 700; color: var(--rw-accent); flex-shrink: 0; }
.rdm-steps { margin: 4px 0 16px; padding: 0; list-style: none; }
.rdm-steps li { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 13px; font-size: 14.5px; line-height: 1.45; }
.rdm-n { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: var(--rw-accent-soft); color: var(--rw-accent); font-size: 12.5px; font-weight: 800; }
.rdm-note { font-size: 12.5px; opacity: .6; line-height: 1.5; margin: 0 0 14px; }
.rdm-btn { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px; border: none; border-radius: 13px; padding: 14px 18px; font-size: 15px; font-weight: 700; cursor: pointer; font-family: inherit; background: var(--rw-accent); color: #fff; margin-bottom: 10px; }
.rdm-btn.ghost { background: transparent; color: inherit; border: 1px solid var(--rdm-border); }
.rdm-email { font-weight: 700; word-break: break-all; }
`;

const RedeemModal = (props: Props) => {
  const { reward, redemption, canOnline } = props;
  const canInStore = redemption.inStore;
  const [step, setStep] = React.useState<"choose" | "instore">("choose");
  const left = Math.max(0, props.available - reward.points);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && props.onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const style = {
    ...props.vars,
    "--rdm-border": props.borderColor,
    backgroundColor: props.cardColor,
    color: props.textColor,
  } as React.CSSProperties;

  return ReactDOM.createPortal(
    <div className="rdm-back" style={props.vars} onClick={props.onClose}>
      <style>{css}</style>
      <div className="rdm" style={style} role="dialog" aria-label={`Redeem ${props.productName}`} onClick={(e) => e.stopPropagation()}>
        <div className="rdm-figure">
          {props.image ? <img src={props.image} alt="" /> : <HiGift className="rdm-gift" />}
          <button className="rdm-x" onClick={props.onClose} aria-label="Close">
            <HiX />
          </button>
        </div>
        <div className="rdm-body">
          <h2 className="rdm-name">{props.productName}</h2>
          <div className="rdm-pts">
            <span className="rdm-pill">
              <HiStar /> {reward.points.toLocaleString()} pts
            </span>
            <span>
              You have {props.available.toLocaleString()} · {left.toLocaleString()} left after
            </span>
          </div>

          {step === "choose" && (
            <>
              <div className="rdm-q">HOW WOULD YOU LIKE IT?</div>
              {canOnline && (
                <button className="rdm-opt primary" onClick={props.onAddToCart}>
                  <span className="rdm-opt-icon">
                    <HiShoppingCart />
                  </span>
                  <span className="rdm-opt-text">
                    <b>Add to cart</b>
                    <span className="rdm-opt-sub">Comes with your online order — points are used when you pay.</span>
                  </span>
                  <span className="rdm-opt-go">›</span>
                </button>
              )}
              {canInStore && (
                <button className={`rdm-opt ${canOnline ? "" : "primary"}`} onClick={() => setStep("instore")}>
                  <span className="rdm-opt-icon">
                    <HiOfficeBuilding />
                  </span>
                  <span className="rdm-opt-text">
                    <b>Redeem in store</b>
                    <span className="rdm-opt-sub">Claim it at the counter on your next visit.</span>
                  </span>
                  <span className="rdm-opt-go">›</span>
                </button>
              )}
            </>
          )}

          {step === "instore" && (
            <>
              <div className="rdm-q">REDEEM IN STORE</div>
              <ol className="rdm-steps">
                {redemption.inStoreQr ? (
                  <li>
                    <span className="rdm-n">1</span>
                    <span>At the till, scan the QR code on the screen facing you.</span>
                  </li>
                ) : (
                  <li>
                    <span className="rdm-n">1</span>
                    <span>
                      At the till, give staff the email on your account: <span className="rdm-email">{props.email}</span>
                    </span>
                  </li>
                )}
                <li>
                  <span className="rdm-n">2</span>
                  <span>
                    Tell them you'd like to redeem <b>{props.productName}</b>.
                  </span>
                </li>
                <li>
                  <span className="rdm-n">3</span>
                  <span>They'll take {reward.points.toLocaleString()} points from your balance.</span>
                </li>
              </ol>
              <p className="rdm-note">Nothing is taken from your points until staff redeem it at the till.</p>
              {redemption.inStoreQr && (
                <button className="rdm-btn" onClick={props.onScan}>
                  <HiQrcode /> Scan the till's QR code
                </button>
              )}
              <button className={`rdm-btn ${redemption.inStoreQr ? "ghost" : ""}`} onClick={props.onClose}>
                Got it
              </button>
              {canOnline && (
                <button className="rdm-btn ghost" onClick={() => setStep("choose")}>
                  Back
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RedeemModal;
